import { useQuery } from '@tanstack/react-query'
import { getTypeaheadSuggestions } from 'api/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { orderAirportTransferSuggestions } from 'lib/airportTransfer/airportTransferUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'

const AIRPORT_TYPES: Array<App.SearchPlaceType> = ['airport']
const AIRPORT_SEARCH_TYPES: Array<App.SearchType> = ['airport']

function useAirportTransferLocationSuggestions(searchPhrase: string) {
  const searchTerm = searchPhrase.trim()
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const identity = JSON.stringify([searchTerm, region])
  const [debouncedIdentity, setDebouncedIdentity] = useState('')
  const shouldSearch = searchTerm.length >= 3

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedIdentity(identity), 300)
    return () => clearTimeout(timeout)
  }, [identity])

  const isDebouncing = identity !== debouncedIdentity
  const query = useQuery({
    queryKey: ['airport-transfer-location-suggestions', searchTerm, region],
    queryFn: () => getTypeaheadSuggestions(searchTerm, region, {
      types: AIRPORT_TYPES,
      searchTypes: AIRPORT_SEARCH_TYPES,
      vertical: 'hotel',
      priority: 'next',
      limit: 20,
    }),
    enabled: shouldSearch && !isDebouncing,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const isLoading = shouldSearch && (isDebouncing || query.isFetching)
  const isSuccess = shouldSearch && !isLoading && query.isSuccess
  const items = useMemo(() => isSuccess
    ? orderAirportTransferSuggestions(query.data, searchTerm)
    : EmptyArray, [isSuccess, query.data, searchTerm])
  const { refetch } = query
  const retry = useCallback(async() => {
    await refetch()
  }, [refetch])

  return {
    items,
    isLoading,
    isSuccess,
    error: shouldSearch && !isLoading ? query.error : undefined,
    retry,
  }
}

export default useAirportTransferLocationSuggestions
